import { ChangeDetectionStrategy, Component, Inject, OnInit, inject } from '@angular/core';
import { OktaService } from './services/okta/okta.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BYPASS_AUTH_INTERCEPTOR, BYPASS_XSRF_AUTH_INTERCEPTOR } from './app.module';
import { firstValueFrom } from 'rxjs';
import { FundDataService } from './services/fund-id/fund-id.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  showSignOut = true;
  private readonly fundDataService = inject(FundDataService);

  // eslint-disable-next-line @typescript-eslint/max-params
  constructor(
    private router: Router,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private oktaService: OktaService,
    private cookieService: CookieService,
    private http: HttpClient,
  ) {
    this.oktaAuth.authStateManager.subscribe((authState) => {
      if (authState.isAuthenticated) {
        const accessToken = authState.accessToken?.accessToken;

        if (accessToken) {
          this.showSignOut = true;
          this.oktaService.getAccessTokenObservable().next(accessToken);
          // Initialize tokens and load fund IDs
          void this.initializeApp();
        }
      } else {
        this.showSignOut = false;
      }
    });
  }

  async ngOnInit() {
    // this.performLogin();
  }

  /**
   * Initialize app: tokens first, then fund IDs
   */
  private async initializeApp(): Promise<void> {
    try {
      // Step 1: Initialize XSRF token
      await this.initializeXsrfToken();
      // Step 2: Get OAuth token
      await this.getOAuthTokenFromOktaToken();
      // Step 3: Load fund IDs
      await this.loadFundIds();
    } catch (error) {
      // Log initialization errors but allow app to continue
      if (error instanceof Error) {
        console.error('[App Init] Failed to initialize:', error.message);
      }
    }
  }

  private async initializeXsrfToken(): Promise<void> {
    let context = new HttpContext();
    context = context.set(BYPASS_XSRF_AUTH_INTERCEPTOR, true);
    context = context.set(BYPASS_AUTH_INTERCEPTOR, true);

    await firstValueFrom(
      this.http.get(`${environment.STATEMENT_MESSAGING.BASE_URL}/xsrf-token`, {
        context: context,
        withCredentials: true,
      }),
    );
  }

  private async getOAuthTokenFromOktaToken(): Promise<void> {
    let context = new HttpContext();
    context = context.set(BYPASS_XSRF_AUTH_INTERCEPTOR, false);
    context = context.set(BYPASS_AUTH_INTERCEPTOR, false);

    interface JwtExchangeResponse {
      access_token: string;
    }

    const response = await firstValueFrom(
      this.http.post<JwtExchangeResponse>(
        `${environment.STATEMENT_MESSAGING.BASE_URL}${environment.STATEMENT_MESSAGING.JWT_EXCHANGE_TOKEN}`,
        {},
        { context: context },
      ),
    );

    this.oktaService.getCaTokenSubject().next(response.access_token);
  }

  /**
   * Load fund IDs after authentication is complete
   */
  private async loadFundIds(): Promise<void> {
    try {
      await firstValueFrom(this.fundDataService.loadFundIds());
    } catch (error) {
      // Don't throw - app should still work without fund IDs
      if (error instanceof Error) {
        console.error('[App Init] Failed to load fund IDs:', error.message);
      }
    }
  }

  async performLogout() {
    this.oktaAuth.clearStorage();
    this.cookieService.delete('XSRF-Token', '/');
    await this.router.navigateByUrl('login-error');
  }

  async performOktaLogin() {
    const routerUrl = this.router.url.split('=')[1];
    const targetUrl = decodeURIComponent(routerUrl);
    if (targetUrl === 'undefined' || targetUrl === '') {
      await this.oktaService.performLogin('/');
    } else {
      await this.oktaService.performLogin(targetUrl);
    }
  }


  async handleLogout(): Promise<void> {
    await this.performLogout();
  }

  async handleLogin(): Promise<void> {
    await this.performOktaLogin();
  }
}
